/**
 * Onboarding Analytics Dashboard
 * ===============================
 * Analytics dashboard for tracking onboarding performance.
 * Shows conversion funnel, geographic trends, and color preferences.
 */

import React, { useState, useEffect } from 'react';
import {
  BarChart3, Globe, TrendingUp, Users, Palette, ArrowUpRight, ArrowDownRight,
  RefreshCw, Calendar, MapPin
} from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// Funnel Stage Component
const FunnelStage = ({ label, count, percentage, isFirst }) => (
  <div className="relative">
    <div 
      className="bg-gradient-to-r from-stone-800 to-stone-700 text-white px-4 py-3 text-center"
      style={{ 
        width: `${Math.max(percentage, 30)}%`,
        marginLeft: isFirst ? '0' : 'auto',
        marginRight: isFirst ? 'auto' : '0'
      }}
    >
      <div className="font-semibold">{count.toLocaleString()}</div>
      <div className="text-xs text-stone-300">{label}</div>
    </div>
    {!isFirst && (
      <div className="absolute -top-6 right-0 text-xs text-stone-500">
        {percentage.toFixed(1)}%
      </div>
    )}
  </div>
);

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, change, changeType }) => (
  <div className="bg-white border border-stone-200 p-5">
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 bg-stone-100 flex items-center justify-center">
        <Icon className="w-5 h-5 text-stone-600" />
      </div>
      {change !== undefined && (
        <div className={`flex items-center text-sm ${
          changeType === 'positive' ? 'text-green-600' : 
          changeType === 'negative' ? 'text-red-600' : 'text-stone-500'
        }`}>
          {changeType === 'positive' ? <ArrowUpRight className="w-4 h-4" /> : 
           changeType === 'negative' ? <ArrowDownRight className="w-4 h-4" /> : null}
          {change}%
        </div>
      )}
    </div>
    <div className="text-2xl font-semibold text-stone-900">{value}</div>
    <div className="text-sm text-stone-500">{label}</div>
  </div>
);

// Color Bar Component
const ColorBar = ({ color, count, maxCount }) => {
  const colorStyles = {
    White: 'bg-gradient-to-r from-gray-100 to-white border border-gray-200',
    Beige: 'bg-gradient-to-r from-amber-100 to-orange-50',
    Grey: 'bg-gradient-to-r from-gray-400 to-gray-300',
    Black: 'bg-gradient-to-r from-gray-900 to-gray-700',
    Green: 'bg-gradient-to-r from-emerald-600 to-green-500',
    Blue: 'bg-gradient-to-r from-[#4A5568] to-[#718096]',
    Brown: 'bg-gradient-to-r from-amber-700 to-yellow-600',
    Red: 'bg-gradient-to-r from-red-600 to-rose-500',
  };
  
  const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
  
  return (
    <div className="flex items-center gap-3 mb-2">
      <div className="w-20 text-sm text-stone-600">{color}</div>
      <div className="flex-1 h-6 bg-stone-100 overflow-hidden">
        <div 
          className={`h-full ${colorStyles[color] || 'bg-stone-400'}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="w-12 text-sm text-stone-600 text-right">{count}</div>
    </div>
  );
};

// Geographic Trends Table
const GeoTrendsTable = ({ data }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-stone-200">
          <th className="text-left py-3 px-2 font-medium text-stone-600">Country</th>
          <th className="text-right py-3 px-2 font-medium text-stone-600">Events</th>
          <th className="text-right py-3 px-2 font-medium text-stone-600">Selections</th>
          <th className="text-right py-3 px-2 font-medium text-stone-600">Conversions</th>
          <th className="text-right py-3 px-2 font-medium text-stone-600">Abandons</th>
          <th className="text-right py-3 px-2 font-medium text-stone-600">Rate</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row, idx) => (
          <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50">
            <td className="py-3 px-2 font-medium text-stone-900">
              <span className="inline-flex items-center gap-2">
                <MapPin className="w-3 h-3 text-stone-400" />
                {row.country}
              </span>
            </td>
            <td className="py-3 px-2 text-right text-stone-600">{row.total_events}</td>
            <td className="py-3 px-2 text-right text-stone-600">{row.color_selections}</td>
            <td className="py-3 px-2 text-right text-green-600">{row.completions}</td>
            <td className="py-3 px-2 text-right text-red-600">{row.abandons}</td>
            <td className="py-3 px-2 text-right font-medium">
              <span className={row.conversion_rate > 50 ? 'text-green-600' : 'text-stone-600'}>
                {row.conversion_rate}%
              </span>
            </td>
          </tr>
        ))}
        {data.length === 0 && (
          <tr>
            <td colSpan={6} className="py-8 text-center text-stone-400">
              No geographic data yet
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

// Main Dashboard Component
const OnboardingAnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  const fetchAnalytics = async () => {
    try {
      const [analyticsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/onboarding/analytics`),
        fetch(`${API}/api/onboarding/stats`)
      ]);
      
      if (analyticsRes.ok && statsRes.ok) {
        setAnalytics(await analyticsRes.json());
        setStats(await statsRes.json());
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  useEffect(() => {
    fetchAnalytics();
  }, []);
  
  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-stone-400 animate-spin" />
      </div>
    );
  }
  
  const funnel = analytics?.funnel || {};
  const maxFunnelValue = Math.max(funnel.views || 1, 1);
  
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-stone-900">Onboarding Analytics</h2>
          <p className="text-stone-500">Track user engagement and conversion rates</p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Sessions"
          value={stats?.total_sessions || 0}
        />
        <StatCard
          icon={Palette}
          label="Color Selections"
          value={stats?.color_selections || 0}
        />
        <StatCard
          icon={TrendingUp}
          label="Conversion Rate"
          value={`${stats?.conversion_rate || 0}%`}
          change={stats?.conversion_rate || 0}
          changeType={stats?.conversion_rate > 30 ? 'positive' : 'neutral'}
        />
        <StatCard
          icon={BarChart3}
          label="Abandon Rate"
          value={`${stats?.abandon_rate || 0}%`}
          change={stats?.abandon_rate || 0}
          changeType={stats?.abandon_rate < 20 ? 'positive' : 'negative'}
        />
      </div>
      
      {/* Conversion Funnel */}
      <div className="bg-white border border-stone-200 p-6">
        <h3 className="text-lg font-semibold text-stone-900 mb-6">Conversion Funnel</h3>
        <div className="space-y-3">
          <FunnelStage 
            label="Views" 
            count={funnel.views || 0} 
            percentage={100} 
            isFirst 
          />
          <FunnelStage 
            label="Color Selections" 
            count={funnel.color_selections || 0} 
            percentage={(funnel.color_selections / maxFunnelValue) * 100} 
          />
          <FunnelStage 
            label="Image Uploads" 
            count={funnel.image_uploads || 0} 
            percentage={(funnel.image_uploads / maxFunnelValue) * 100} 
          />
          <FunnelStage 
            label="Material Views" 
            count={funnel.material_views || 0} 
            percentage={(funnel.material_views / maxFunnelValue) * 100} 
          />
          <FunnelStage 
            label="Signups" 
            count={funnel.signups || 0} 
            percentage={(funnel.signups / maxFunnelValue) * 100} 
          />
        </div>
        
        <div className="mt-6 pt-4 border-t border-stone-100 flex items-center justify-between text-sm">
          <span className="text-stone-500">Abandons</span>
          <span className="text-red-600 font-medium">{funnel.abandons || 0}</span>
        </div>
      </div>
      
      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Colors */}
        <div className="bg-white border border-stone-200 p-6">
          <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5 text-stone-400" />
            Popular Colors
          </h3>
          <div>
            {stats?.popular_colors?.length > 0 ? (
              stats.popular_colors.map((item, idx) => (
                <ColorBar
                  key={idx}
                  color={item.color}
                  count={item.count}
                  maxCount={stats.popular_colors[0]?.count || 1}
                />
              ))
            ) : (
              <p className="text-stone-400 text-center py-8">No color data yet</p>
            )}
          </div>
        </div>
        
        {/* Daily Trends */}
        <div className="bg-white border border-stone-200 p-6">
          <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-stone-400" />
            Daily Trends (Last 30 Days)
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {analytics?.daily_trends?.length > 0 ? (
              analytics.daily_trends.slice(-14).map((day, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-stone-50">
                  <span className="text-sm text-stone-600">{day.date}</span>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-stone-500">{day.views} views</span>
                    <span className="text-green-600">{day.completions} conv.</span>
                    <span className={day.conversion_rate > 30 ? 'text-green-600 font-medium' : 'text-stone-400'}>
                      {day.conversion_rate}%
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-stone-400 text-center py-8">No daily data yet</p>
            )}
          </div>
        </div>
      </div>
      
      {/* Geographic Trends */}
      <div className="bg-white border border-stone-200 p-6">
        <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-stone-400" />
          Geographic Trends
        </h3>
        <GeoTrendsTable data={analytics?.geo_trends || []} />
      </div>
      
      {/* Color Preferences by Region */}
      {analytics?.color_preferences?.length > 0 && (
        <div className="bg-white border border-stone-200 p-6">
          <h3 className="text-lg font-semibold text-stone-900 mb-4">Color Preferences by Region</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {analytics.color_preferences.slice(0, 12).map((pref, idx) => (
              <div key={idx} className="p-3 bg-stone-50 border border-stone-100">
                <div className="text-sm text-stone-500">{pref.country}</div>
                <div className="font-medium text-stone-900">{pref.color}</div>
                <div className="text-xs text-stone-400">{pref.count} selections</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingAnalyticsDashboard;
